export const onlyDigits = (s?: string) => (s || '').replace(/\D+/g, '');

export const onlyLettersAndDigits = (s?: string) =>
  (s || '').replace(/[^A-Za-z0-9]+/g, '').toUpperCase();

export const normalizeCpfCnpj = (s?: string) => onlyLettersAndDigits(s);

const isAsciiDigit = (value: string) => /^[0-9]$/.test(value);

const hasRepeatedCharacters = (value: string) =>
  value.length > 0 && value.split('').every((char) => char === value[0]);

const getCnpjCharacterValue = (value: string) => value.charCodeAt(0) - 48;

export const isValidCpfCnpj = (value: string) => {
  const document = normalizeCpfCnpj(value);
  if (!document) return false;

  if (document.length === 11 && document.split('').every(isAsciiDigit)) {
    return isValidCpf(document);
  }

  if (document.length === 14) return isValidCnpj(document);
  return false;
};

export const isValidCpf = (cpf: string) => {
  const document = onlyDigits(cpf);
  if (!document || document.length !== 11 || hasRepeatedCharacters(document)) {
    return false;
  }

  const m1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
  const m2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];

  let temp = document.substring(0, 9);
  let sum = temp
    .split('')
    .reduce((acc, n, i) => acc + parseInt(n, 10) * m1[i], 0);
  let r = sum % 11;
  const d1 = r < 2 ? 0 : 11 - r;

  temp += String(d1);
  sum = temp.split('').reduce((acc, n, i) => acc + parseInt(n, 10) * m2[i], 0);
  r = sum % 11;
  const d2 = r < 2 ? 0 : 11 - r;

  return document.endsWith(`${d1}${d2}`);
};

export const isValidCnpj = (cnpj: string) => {
  const document = normalizeCpfCnpj(cnpj);
  if (
    !document ||
    document.length !== 14 ||
    hasRepeatedCharacters(document) ||
    !/^[A-Z0-9]{12}[0-9]{2}$/.test(document)
  ) {
    return false;
  }

  const m1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const m2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let temp = document.substring(0, 12);
  let sum = temp
    .split('')
    .reduce((acc, n, i) => acc + getCnpjCharacterValue(n) * m1[i], 0);
  let r = sum % 11;
  const d1 = r < 2 ? 0 : 11 - r;

  temp += String(d1);
  sum = temp
    .split('')
    .reduce((acc, n, i) => acc + getCnpjCharacterValue(n) * m2[i], 0);
  r = sum % 11;
  const d2 = r < 2 ? 0 : 11 - r;

  return document.endsWith(`${d1}${d2}`);
};

export const formatCpf = (value: string) => {
  const s = onlyDigits(value).slice(0, 11);
  if (s.length > 9) {
    return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(
      9,
      11
    )}`;
  }
  if (s.length > 6) return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6)}`;
  if (s.length > 3) return `${s.slice(0, 3)}.${s.slice(3)}`;
  return s;
};

export const formatCnpj = (value: string) => {
  const s = normalizeCpfCnpj(value).slice(0, 14);
  if (s.length > 12) {
    return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(
      8,
      12
    )}-${s.slice(12, 14)}`;
  }
  if (s.length > 8) {
    return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8)}`;
  }
  if (s.length > 5) return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5)}`;
  if (s.length > 2) return `${s.slice(0, 2)}.${s.slice(2)}`;
  return s;
};

export const formatCpfCnpj = (value?: string | number) => {
  const normalized = normalizeCpfCnpj(String(value ?? ''));
  const isCnpj = /[A-Z]/.test(normalized) || normalized.length > 11;
  return isCnpj ? formatCnpj(normalized) : formatCpf(normalized);
};
